/**
 * Utility functions for lateness calculations and data processing
 * Similar to volumeDeltaUtils but for timing/lateness metrics
 */

import {
  calculateAverage as calcAvg,
  calculateTrend as calcTrend,
  calculateLinearRegression,
  filterSessionsByDays,
  filterSessionsByType,
  getLastNSessions,
  getExercisesByType
} from './volumeDeltaUtils';

// Re-export common utilities
export { filterSessionsByDays, filterSessionsByType, getLastNSessions, getExercisesByType, calculateLinearRegression };

/**
 * Calculate average of a lateness field across sessions
 */
export const calculateAverage = (sessions, field) => {
  return calcAvg(sessions, field);
};

/**
 * Calculate trend for lateness (last 5 avg vs last 30 avg)
 */
export const calculateTrend = (sessions, field) => {
  return calcTrend(sessions, field);
};

/**
 * Get earliest and latest finger for a hand (most early/late timing)
 */
export const getEarliestLatestFingers = (sessions, hand) => {
  if (!sessions || sessions.length === 0) {
    return { earliest: null, latest: null };
  }
  
  const fingerAverages = [];
  
  for (let i = 1; i <= 5; i++) {
    const field = `${hand}_finger_${i}_lateness_avg`;
    const avg = calculateAverage(sessions, field);
    if (avg !== null) {
      fingerAverages.push({ finger: i, avg });
    }
  }
  
  if (fingerAverages.length === 0) {
    return { earliest: null, latest: null };
  }
  
  // Sort by value (negative = early, positive = late)
  fingerAverages.sort((a, b) => a.avg - b.avg);
  
  const earliest = fingerAverages[0]; // Most negative (earliest)
  const latest = fingerAverages[fingerAverages.length - 1]; // Most positive (latest)
  
  return { earliest, latest };
};

/**
 * Prepare data for trend over time scatter plot
 */
export const prepareTrendOverTimeData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_lateness_avg`;
    const points = sessions
      .filter(s => s[field] != null)
      .map(s => ({
        x: new Date(s.created_at).getTime(),
        y: s[field],
        date: s.created_at
      }));
    
    const regression = calculateLinearRegression(points);
    
    data[finger] = { points, regression };
  });
  
  return data;
};

/**
 * Prepare data for trend over tempo scatter plot
 */
export const prepareTrendOverTempoData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_lateness_avg`;
    const points = sessions
      .filter(s => s[field] != null && s.calculated_tempo != null)
      .map(s => ({
        x: s.calculated_tempo,
        y: s[field]
      }));
    
    data[finger] = { points };
  });
  
  return data;
};

/**
 * Prepare data for trend over repetition scatter plot
 */
export const prepareTrendOverRepetitionData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_lateness_avg`;
    const points = sessions
      .filter(s => s[field] != null)
      .map((s, idx) => ({
        x: sessions.length - idx,
        y: s[field],
        date: s.created_at
      }))
      .reverse();
    
    const regression = calculateLinearRegression(points);
    
    data[finger] = { points, regression };
  });
  
  return data;
};

/**
 * Calculate opacity for heatmap cell based on lateness value
 * Different scale than volume delta (timing is typically smaller values)
 */
export const calculateHeatmapOpacity = (value) => {
  if (value == null) return 0;
  const absValue = Math.abs(value);
  // Scale for lateness: 0-50ms range
  return Math.min(absValue / 50, 1);
};

/**
 * Format lateness value for display
 */
export const formatLateness = (lateness) => {
  if (lateness == null) return 'N/A';
  
  const absLateness = Math.abs(lateness);
  const timing = lateness > 0 ? 'late' : 'early';
  
  return `${absLateness.toFixed(1)}ms ${timing}`;
};

/**
 * Calculate synchronization (difference between left and right hand timing)
 */
export const calculateSynchronization = (sessions) => {
  if (!sessions || sessions.length === 0) return null;
  
  const validSessions = sessions.filter(s => 
    s.left_lateness_avg != null && s.right_lateness_avg != null
  );
  
  if (validSessions.length === 0) return null;
  
  const sum = validSessions.reduce((acc, s) => 
    acc + (s.right_lateness_avg - s.left_lateness_avg), 0
  );
  
  return sum / validSessions.length;
};

/**
 * Calculate median of a field across sessions
 */
export const calculateMedian = (sessions, field) => {
  if (!sessions || sessions.length === 0) return null;
  
  const values = sessions
    .filter(s => s[field] != null)
    .map(s => s[field])
    .sort((a, b) => a - b);
  
  if (values.length === 0) return null;
  
  const mid = Math.floor(values.length / 2);
  return values.length % 2 === 0
    ? (values[mid - 1] + values[mid]) / 2
    : values[mid];
};

/**
 * Calculate percentage of sessions where value is within range
 */
export const calculateInRangePercentage = (sessions, field, threshold) => {
  if (!sessions || sessions.length === 0) return null;
  
  const validSessions = sessions.filter(s => s[field] != null);
  if (validSessions.length === 0) return null;
  
  const inRange = validSessions.filter(s => 
    Math.abs(s[field]) <= threshold
  ).length;
  
  return (inRange / validSessions.length) * 100;
};

/**
 * Get worst offenders for synchronization (fingers with highest std dev in lateness)
 */
export const getWorstOffendersSync = (sessions, hand) => {
  if (!sessions || sessions.length === 0) {
    return { worst: null };
  }
  
  const fingerSyncValues = [];
  
  for (let i = 1; i <= 5; i++) {
    const stdDevField = `${hand}_finger_${i}_lateness_std_dev`;
    const avgStdDev = calculateAverage(sessions, stdDevField);
    if (avgStdDev !== null) {
      fingerSyncValues.push({ finger: i, avg: avgStdDev });
    }
  }
  
  if (fingerSyncValues.length === 0) {
    return { worst: null };
  }
  
  // Sort by std dev (higher = worse synchronization)
  fingerSyncValues.sort((a, b) => b.avg - a.avg);
  
  const worst = fingerSyncValues[0]; // Highest std dev
  
  return { worst };
};

/**
 * Calculate timing score for a finger/hand (average of abs(lateness) and sync)
 * Lower is better
 */
export const calculateTimingScore = (sessions, entity) => {
  if (!sessions || sessions.length === 0) return null;
  
  const latenessField = `${entity}_lateness_avg`;
  const syncField = `${entity}_lateness_std_dev`;
  
  const validSessions = sessions.filter(s => 
    s[latenessField] != null && s[syncField] != null
  );
  
  if (validSessions.length === 0) return null;
  
  const scores = validSessions.map(s => {
    const absLateness = Math.abs(s[latenessField]);
    const sync = s[syncField];
    return (absLateness + sync) / 2;
  });
  
  return scores.reduce((acc, val) => acc + val, 0) / scores.length;
};

/**
 * Calculate bidirectional color for timing score heatmap
 * Orange for poor timing (high score), purple for good timing (low score), white for middle
 */
export const calculateTimingScoreColor = (score) => {
  if (score == null) return 'rgb(255, 255, 255)'; // white for no data
  
  // Normalize score: 0-50ms range
  const intensity = Math.min(score / 50, 1);
  
  if (score > 15) {
    // Poor timing - orange (more intense as score increases)
    // Orange color: rgb(234, 88, 12) or #ea580c
    const r = 255;
    const g = Math.round(255 - (255 - 88) * intensity);
    const b = Math.round(255 - (255 - 12) * intensity);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (score < 5) {
    // Excellent timing - purple (more intense as score decreases)
    // Purple color: rgb(147, 51, 234) or #9333ea
    const purpleIntensity = Math.max(0, (5 - score) / 5);
    const r = Math.round(255 - (255 - 147) * purpleIntensity);
    const g = Math.round(255 - (255 - 51) * purpleIntensity);
    const b = 255;
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Medium timing - gradient from white to light orange
    const midIntensity = (score - 5) / 10;
    const r = 255;
    const g = Math.round(255 - (255 - 200) * midIntensity);
    const b = Math.round(255 - (255 - 200) * midIntensity);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

/**
 * Prepare synchronization trend data over repetition
 */
export const prepareSyncTrendOverRepetitionData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_lateness_std_dev`;
    const points = sessions
      .filter(s => s[field] != null)
      .map((s, idx) => ({
        x: sessions.length - idx,
        y: s[field],
        date: s.created_at
      }))
      .reverse();
    
    const regression = calculateLinearRegression(points);
    
    data[finger] = { points, regression };
  });
  
  return data;
};

